import { RubyGemMark } from '../../components/RubyGemMark';
import React from 'react';
import {
  Check, Clock, X, FileText, Hash, Landmark, BadgeCheck, UserSquare, PenLine, Download, Upload, Trash2,
  CheckCircle2, XCircle,
} from 'lucide-react';

/**
 * THE COMPLIANCE PACK, AS MARKS.
 *
 * Less words, more graphics: a tick, a clock, an empty circle, a cross. What
 * somebody scanning this needs is how many are in — the status sentence is on
 * the tooltip for when they actually want it.
 *
 * Shared by the location card and the Business documents stage so the two
 * cannot drift into showing the same pack two different ways.
 */

export interface PackDocument {
  documentId: string;
  name: string;
  status: string;
  required?: boolean;
  comment?: string;
}

const STATE: Record<string, { mark: React.ReactNode; ring: string; title: string }> = {
  verified: { mark: <Check size={11} strokeWidth={3} />, ring: 'bg-emerald-500 text-white', title: 'Verified' },
  accepted: { mark: <Check size={11} strokeWidth={3} />, ring: 'bg-emerald-500 text-white', title: 'Verified' },
  uploaded: { mark: <Clock size={10} />, ring: 'bg-blue-500 text-white', title: 'Uploaded — awaiting review' },
  requested: { mark: null, ring: 'bg-white border border-stone-300', title: 'Not uploaded' },
  rejected: { mark: <X size={11} strokeWidth={3} />, ring: 'bg-red-500 text-white', title: 'Rejected' },
};

/** A glyph per document, so the pack reads at a glance rather than by name. */
export const docIcon = (name: string): React.ReactNode => {
  const n = (name || '').toLowerCase();
  if (n.includes('ein') || n.includes('tax')) return <Hash size={13} />;
  if (n.includes('article') || n.includes('incorporat')) return <Landmark size={13} />;
  if (n.includes('licen')) return <BadgeCheck size={13} />;
  if (n.includes('identity') || n.includes('owner')) return <UserSquare size={13} />;
  if (n.includes('dba')) return <PenLine size={13} />;
  if (n.includes('cheque') || n.includes('check') || n.includes('bank')) return <Landmark size={13} />;
  return <FileText size={13} />;
};

const DocumentMarks: React.FC<{
  documents: PackDocument[];
  onOpen?: (documentId: string) => void;
  /** The segmented bar above the list. Off inside a stage, where space is tight. */
  showBar?: boolean;
  /**
   * UPLOAD ON THE MERCHANT'S BEHALF. Optional, and absent by default: the
   * location card renders this same pack read-only, and only the stage that
   * owns the work hands in a handler. A merchant who cannot work the uploader
   * and emails the file is the ordinary reason.
   */
  onUpload?: (documentId: string, file: File) => Promise<void>;
  /** The documentId currently uploading, so its row alone shows the spinner. */
  uploading?: string | null;
  /** Take the file off, back to "not uploaded". Never offered on a verified document. */
  onDelete?: (documentId: string) => Promise<void>;
  /**
   * APPROVE OR REJECT — the decision this list existed to inform and could
   * not make. The pack sat on "uploaded, awaiting review" with no way to
   * review it from the order, so an operator had to go and find the location
   * request. Rejecting requires a reason: the merchant is shown it word for
   * word, and a re-upload puts the document back to awaiting.
   *
   * The PARTNER has the same two controls on their own screen. Both write the
   * same two statuses, so a document cannot mean different things depending
   * on who cleared it.
   */
  onDecide?: (documentId: string, action: 'approve' | 'reject', comment?: string) => Promise<void>;
  /** The documentId currently being decided. */
  deciding?: string | null;
}> = ({ documents, onOpen, showBar = true, onUpload, uploading, onDelete, onDecide, deciding }) => {
  // The row being rejected, and the reason typed into it.
  const [rejecting, setRejecting] = React.useState<string | null>(null);
  const [reason, setReason] = React.useState('');
  const required = documents.filter((d) => d.required !== false);
  // Two taps, never window.confirm(): the first arms the row, the second clears.
  const [confirming, setConfirming] = React.useState<string | null>(null);

  if (documents.length === 0) {
    return <p className="text-[12px] text-stone-400">Nothing requested yet.</p>;
  }

  return (
    <>
      {showBar && required.length > 0 && (
        <div className="h-1.5 rounded-full bg-stone-200 overflow-hidden mb-2.5 flex">
          {required.map((d) => (
            <span
              key={d.documentId}
              title={`${d.name} — ${(STATE[d.status] || { title: d.status }).title}`}
              className={`flex-1 border-r-2 border-white last:border-0 ${
                d.status === 'verified' || d.status === 'accepted' ? 'bg-emerald-500'
                : d.status === 'uploaded' ? 'bg-blue-500'
                : d.status === 'rejected' ? 'bg-red-500' : 'bg-transparent'}`}
            />
          ))}
        </div>
      )}

      <div className="space-y-1">
        {documents.map((d) => {
          const st = STATE[d.status] || { mark: null, ring: 'bg-stone-300', title: d.status };
          const hasFile = d.status !== 'requested';
          return (
            // A Fragment, because the reason box is a SECOND row under this
            // one and must not be laid out inside a flex row of controls.
            <React.Fragment key={d.documentId}>
            <div
              title={`${d.name} — ${st.title}${d.comment ? ` · ${d.comment}` : ''}`}
              className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-stone-50"
            >
              <span className={`w-[17px] h-[17px] rounded-full grid place-items-center shrink-0 ${st.ring}`}>
                {st.mark}
              </span>
              <span className="text-stone-400 shrink-0">{docIcon(d.name)}</span>
              <span className="text-[12.5px] text-stone-700 flex-1 min-w-0 truncate">{d.name}</span>
              {hasFile && onDelete && d.status !== 'verified' && d.status !== 'accepted' && (
                confirming === d.documentId ? (
                  <button
                    onClick={() => { setConfirming(null); void onDelete(d.documentId); }}
                    onBlur={() => setConfirming(null)}
                    autoFocus
                    className="shrink-0 rounded px-2 py-1 text-[11px] font-semibold bg-red-50 text-red-700 hover:bg-red-100"
                  >
                    Remove file?
                  </button>
                ) : (
                  <button
                    onClick={() => setConfirming(d.documentId)}
                    disabled={!!uploading}
                    className="p-1 rounded text-stone-400 hover:text-red-600 shrink-0 disabled:opacity-50"
                    title="Remove this file"
                  >
                    <Trash2 size={12} />
                  </button>
                )
              )}
              {hasFile && onOpen && (
                <button
                  onClick={() => onOpen(d.documentId)}
                  className="p-1 rounded text-stone-400 hover:text-stone-700 shrink-0"
                  title="Open"
                >
                  <Download size={12} />
                </button>
              )}
              {/*
                A LABEL WRAPPING A HIDDEN INPUT — the file picker is the one
                native control there is no substitute for, and this is how it is
                given the same shape as every other button here.

                Offered on anything not yet verified, including a rejection:
                "rejected" is precisely when somebody needs to put a better copy
                up, and that is often the admin doing it for them.
              */}
              {onUpload && d.status !== 'verified' && d.status !== 'accepted' && (
                <label
                  title={d.status === 'requested' ? 'Upload this for the merchant' : 'Replace this file'}
                  className={`shrink-0 cursor-pointer rounded px-1.5 py-1 text-stone-400 hover:bg-white hover:text-rose-600 ${
                    uploading === d.documentId ? 'pointer-events-none opacity-50' : ''}`}
                >
                  {uploading === d.documentId
                    ? <RubyGemMark size={12} drawing />
                    : <Upload size={12} />}
                  <input
                    type="file"
                    className="hidden"
                    accept="application/pdf,image/*"
                    disabled={!!uploading}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      // Cleared so picking the SAME file again still fires a
                      // change event — the ordinary retry after a failure.
                      e.target.value = '';
                      if (file) void onUpload(d.documentId, file);
                    }}
                  />
                </label>
              )}

              {/* THE DECISION. Only once something is up — there is nothing
                  to judge before that. Green tick approves; red cross opens
                  the reason box, because a rejection without one is a closed
                  door rather than a request. */}
              {hasFile && onDecide && (
                <span className="shrink-0 flex items-center gap-0.5 pl-1 ml-0.5 border-l border-stone-200">
                  <button
                    onClick={() => void onDecide(d.documentId, 'approve')}
                    disabled={deciding === d.documentId}
                    title={d.status === 'verified' || d.status === 'accepted' ? 'Approved' : 'Approve'}
                    className={`p-1 rounded disabled:opacity-50 ${
                      d.status === 'verified' || d.status === 'accepted'
                        ? 'text-emerald-600'
                        : 'text-stone-400 hover:text-emerald-600 hover:bg-emerald-50'}`}
                  >
                    {deciding === d.documentId
                      ? <RubyGemMark size={12} drawing color="currentColor" />
                      : <CheckCircle2 size={13} />}
                  </button>
                  <button
                    onClick={() => { setRejecting(rejecting === d.documentId ? null : d.documentId); setReason(''); }}
                    title={d.status === 'rejected' ? 'Rejected' : 'Reject'}
                    className={`p-1 rounded ${
                      d.status === 'rejected' ? 'text-red-600' : 'text-stone-400 hover:text-red-600 hover:bg-red-50'}`}
                  >
                    <XCircle size={13} />
                  </button>
                </span>
              )}
            </div>

            {rejecting === d.documentId && onDecide && (
              <div className="flex gap-2 flex-wrap mt-1.5 mb-1 p-2 rounded-lg bg-red-50 border border-red-200">
                <input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="What is wrong with it? The merchant is shown this."
                  className="flex-1 min-w-[180px] px-2.5 py-1.5 rounded border border-stone-200 bg-white text-[12px] text-stone-800 focus:outline-none focus:border-red-400"
                />
                <button
                  disabled={!reason.trim() || deciding === d.documentId}
                  onClick={() => { void onDecide(d.documentId, 'reject', reason.trim()); setRejecting(null); }}
                  className="px-3 py-1.5 rounded bg-red-600 hover:bg-red-700 text-white text-[12px] font-semibold disabled:opacity-50"
                >
                  Reject
                </button>
                <button
                  onClick={() => setRejecting(null)}
                  className="px-2 py-1.5 rounded border border-stone-200 bg-white text-stone-500 text-[12px]"
                >
                  <X size={12} />
                </button>
              </div>
            )}
            {d.status === 'rejected' && d.comment && rejecting !== d.documentId && (
              <p className="text-[11px] text-red-600 leading-relaxed mb-1">{d.comment}</p>
            )}
          </React.Fragment>
          );
        })}
      </div>
    </>
  );
};

export default DocumentMarks;
